import { z } from 'zod';

import { calendarDateSchema, idSchema, instantSchema, TASK_KEY_PATTERN } from './common';
import { personSchema } from './identity';
import { LIMITS } from './limits';
import { groupSchema, projectPermissionsSchema } from './projects';

/** Contracts of tasks and subtasks — "work items" (api-design §3.5, PRD §9). */

/** A Tiptap document (CRD-4). Structure is validated by the editor; the API checks the envelope. */
export const richTextDocumentSchema = z
  .object({
    type: z.literal('doc'),
    content: z.array(z.record(z.string(), z.unknown())).optional(),
  })
  .loose();
export type RichTextDocument = z.infer<typeof richTextDocumentSchema>;

export const timelineSchema = z
  .object({ start: calendarDateSchema, end: calendarDateSchema })
  .refine((t) => t.end >= t.start, {
    path: ['end'],
    message: 'The end cannot be before the start.',
  });

const titleSchema = z.string().trim().min(1, 'Give the task a title.').max(LIMITS.taskTitleMax);
const notesSchema = z.string().max(LIMITS.notesMax);

/** A work item as shown on the board (TBL, KAN). */
export const taskSchema = z.object({
  id: idSchema,
  key: z.string().regex(TASK_KEY_PATTERN),
  projectId: idSchema,
  groupId: idSchema,
  parentId: idSchema.nullable(),
  title: z.string(),
  statusId: idSchema,
  priorityId: idSchema.nullable(),
  dueDate: calendarDateSchema.nullable(),
  timeline: z.object({ start: calendarDateSchema, end: calendarDateSchema }).nullable(),
  notes: z.string(),
  ownerIds: z.array(idSchema),
  position: z.string(),
  commentCount: z.number().int(),
  fileCount: z.number().int(),
  completedAt: instantSchema.nullable(),
  createdBy: idSchema,
  createdAt: instantSchema,
  updatedBy: idSchema.nullable(),
  updatedAt: instantSchema,
});

/** `GET /projects/{id}/board`: one request for the whole board (sequence S3). */
export const boardResponseSchema = z.object({
  project: z.object({
    id: idSchema,
    name: z.string(),
    isPrivate: z.boolean(),
    archivedAt: instantSchema.nullable(),
    permissions: projectPermissionsSchema,
  }),
  groups: z.array(groupSchema),
  tasks: z.array(taskSchema),
  members: z.array(personSchema),
});

export const taskPermissionsSchema = z.object({
  canEdit: z.boolean(),
  canChangeOwners: z.boolean(),
  canMove: z.boolean(),
  canDelete: z.boolean(),
  canAddSubtasks: z.boolean(),
});

/** `GET /tasks/{idOrKey}`: the task card (CRD-1..5). */
export const taskDetailSchema = taskSchema.extend({
  description: richTextDocumentSchema.nullable(),
  descriptionVersion: z.number().int(),
  project: z.object({ id: idSchema, name: z.string(), archivedAt: instantSchema.nullable() }),
  group: z.object({ id: idSchema, name: z.string(), color: z.string() }),
  subtasks: z.array(taskSchema),
  permissions: taskPermissionsSchema,
});

export const createTaskRequestSchema = z.object({
  projectId: idSchema,
  groupId: idSchema,
  parentId: idSchema.nullable().optional(),
  title: titleSchema,
  statusId: idSchema.optional(),
  priorityId: idSchema.nullable().optional(),
  dueDate: calendarDateSchema.nullable().optional(),
  timeline: timelineSchema.nullable().optional(),
  notes: notesSchema.optional(),
  ownerIds: z.array(idSchema).max(50).optional(),
});

export const updateTaskRequestSchema = z
  .object({
    title: titleSchema.optional(),
    statusId: idSchema.optional(),
    priorityId: idSchema.nullable().optional(),
    dueDate: calendarDateSchema.nullable().optional(),
    timeline: timelineSchema.nullable().optional(),
    notes: notesSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'Change at least one field.' });

export const replaceOwnersRequestSchema = z.object({
  ownerIds: z.array(idSchema).max(50),
});

/** Top-level task: optional new group; subtask: siblings only. `afterId` null = first. */
export const moveTaskRequestSchema = z.object({
  groupId: idSchema.optional(),
  afterId: idSchema.nullable(),
});

export const saveDescriptionRequestSchema = z.object({
  doc: richTextDocumentSchema.nullable(),
  /** The version the edit started from (CRD-4, sequence S9). */
  baseVersion: z.number().int().min(0),
});

export const descriptionSavedSchema = z.object({ version: z.number().int() });

export const taskRefParamsSchema = z.object({
  idOrKey: z.union([idSchema, z.string().regex(TASK_KEY_PATTERN)]),
});

export type Task = z.infer<typeof taskSchema>;
export type TaskDetail = z.infer<typeof taskDetailSchema>;
export type Board = z.infer<typeof boardResponseSchema>;
export type CreateTaskRequest = z.infer<typeof createTaskRequestSchema>;
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;
